/**
 * AI strings (`ai.*`): assistant "Zapytaj NEXOPS", insight cards and the admin usage page.
 * Merged into the dictionary through `extra.ts`; `aiEn` is typed against `aiPl` (a missing key is a compile error).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const aiPl = {
  assistant: {
    title: 'Zapytaj NEXOPS',
    subtitle: 'Asystent AI oparty na Twoich danych',
    open: 'Otwórz asystenta AI',
    close: 'Zamknij asystenta',
    topbar: 'Zapytaj NEXOPS',
    demoChip: 'Tryb demonstracyjny',
    demoChipHint: 'Odpowiedzi są generowane na podstawie szablonów i prawdziwych liczb z systemu.',
    newChat: 'Nowa rozmowa',
    welcomeTitle: 'W czym mogę pomóc?',
    welcomeBody: 'Zadaj pytanie o klientów, sprzedaż, realizację celu czy alerty. Odpowiem na podstawie danych w Twoim zakresie.',
    welcomeBodyLogistics: 'Zadaj pytanie o dostawy, trasy, opóźnienia czy nieudane dostawy. Odpowiem na podstawie danych logistyki.',
    welcomeBodyBoth: 'Zadaj pytanie o sprzedaż, klientów, dostawy czy trasy. Odpowiem na podstawie danych w Twoim zakresie.',
    suggestionsLabel: 'Na początek',
    suggestionGroups: {
      sales: 'Sprzedaż',
      logistics: 'Logistyka',
    },
    placeholder: 'Zadaj pytanie o swoje dane…',
    send: 'Wyślij',
    stop: 'Zatrzymaj',
    copy: 'Kopiuj odpowiedź',
    copied: 'Skopiowano',
    stopped: 'Zatrzymano generowanie odpowiedzi.',
    truncated: 'Odpowiedź została skrócona — dopytaj o szczegóły.',
    retry: 'Spróbuj ponownie',
    thinking: 'Analizuję dane…',
    toolsUsed: 'Sprawdzone dane: {count}',
    toolsRunning: 'Pracuję…',
    you: 'Ty',
    assistantName: 'NEXOPS AI',
    footer: 'Odpowiedzi AI opierają się na danych NEXOPS w Twoim zakresie dostępu.',
    remaining: 'Pozostało dziś: {left} z {limit} pytań',
    usageLink: 'Zużycie i koszt',
    charsLeft: '{count} znaków',
    shortcut: 'Ctrl J',
    scrollDown: 'Przewiń do najnowszej odpowiedzi',
    errors: {
      dailyLimit: 'Wykorzystano dzienny limit pytań ({limit}). Limit odnowi się jutro po północy — do tego czasu możesz korzystać z podsumowań na stronie głównej.',
      dailyLimitNoNumber: 'Wykorzystano dzienny limit pytań. Limit odnowi się jutro po północy.',
      monthlyBudget: 'Miesięczny budżet AI dla organizacji został wyczerpany. Odnowi się na początku następnego miesiąca — w razie pilnej potrzeby skontaktuj się z administratorem.',
      network: 'Nie udało się połączyć z serwerem. Sprawdź połączenie i spróbuj ponownie.',
      unavailable: 'Asystent AI jest chwilowo niedostępny. Spróbuj ponownie za chwilę.',
      refused: 'Asystent nie może odpowiedzieć na to pytanie. Spróbuj sformułować je inaczej.',
      badRequest: 'Nie można wysłać tej wiadomości. Skróć pytanie i spróbuj ponownie.',
      generic: 'Coś poszło nie tak podczas generowania odpowiedzi.',
    },
    suggestions: {
      repCustomers: 'Którzy moi klienci kupują mniej niż miesiąc temu?',
      repToday: 'Co powinienem zrobić dziś?',
      repForecast: 'Jak wygląda moja prognoza?',
      repAlerts: 'Jakie mam najważniejsze alerty?',
      mgrWorstRep: 'Który handlowiec ma najgorszą realizację?',
      mgrWeek: 'Co się zmieniło w tym tygodniu?',
      mgrDeclining: 'Którzy klienci zespołu kupują mniej niż miesiąc temu?',
      mgrForecast: 'Jak wygląda prognoza na koniec miesiąca?',
      boardTop: 'Którzy klienci generują największą sprzedaż w tym miesiącu?',
      boardReceivables: 'Jak wyglądają należności przeterminowane?',
      logLate: 'Ile dostaw jest opóźnionych dziś?',
      logRepeatFailed: 'Którzy klienci mają powtarzające się nieudane dostawy?',
      logOverloaded: 'Które trasy są przeciążone jutro?',
      logWeek: 'Jak wyglądała terminowość dostaw w tym tygodniu?',
    },
    tools: {
      search_customers: 'Szukam klienta…',
      customer_overview: 'Sprawdzam sprzedaż klienta…',
      sales_summary: 'Liczę sprzedaż…',
      top_customers: 'Sprawdzam największych klientów…',
      declining_customers: 'Szukam klientów ze spadkiem…',
      rep_performance: 'Porównuję handlowców…',
      product_stats: 'Sprawdzam sprzedaż produktów…',
      list_alerts: 'Przeglądam alerty…',
      receivables_summary: 'Sprawdzam należności…',
      forecast: 'Liczę prognozę i realizację celu…',
      logistics_deliveries: 'Sprawdzam dostawy…',
      logistics_problems: 'Sprawdzam opóźnione i nieudane dostawy…',
      logistics_problem_customers: 'Szukam klientów z problemami w dostawach…',
      logistics_route_load: 'Sprawdzam obciążenie tras…',
    },
  },
  insight: {
    title: 'Co się zmieniło i dlaczego',
    kind: {
      DAILY_REP: 'Dziś',
      WEEKLY_TEAM: 'Tydzień · zespół',
      WEEKLY_BOARD: 'Tydzień · zarząd',
      LOGISTICS_DAILY: 'Logistyka · dziś',
      LOGISTICS_WEEKLY: 'Logistyka · tydzień',
    },
    logisticsPeriod: 'Okres podsumowania logistyki',
    logisticsDaily: 'Dziś',
    logisticsWeekly: 'Tydzień',
    generatedAt: 'Wygenerowano {when}',
    basis: 'Okres: {period}',
    refresh: 'Odśwież',
    refreshing: 'Odświeżam…',
    actionsTitle: 'Rekomendowane działania',
    actionsHint: 'Odhacz, co już zrobione',
    markDone: 'Oznacz jako zrobione',
    markUndone: 'Oznacz jako do zrobienia',
    askMore: 'Zapytaj o szczegóły',
    emptyTitle: 'Brak podsumowania',
    emptyBody: 'Podsumowanie dla tego okresu nie zostało jeszcze przygotowane. Wygeneruj je teraz.',
    generate: 'Wygeneruj podsumowanie',
    loadError: 'Nie udało się wczytać podsumowania AI.',
    regenLimit: 'Wykorzystano dzienny limit odświeżeń podsumowania. Spróbuj jutro.',
    regenError: 'Nie udało się odświeżyć podsumowania.',
    regenerated: 'Podsumowanie odświeżone',
    model: 'Model: {model}',
  },
  usage: {
    nav: 'AI — zużycie i koszt',
    title: 'AI — zużycie i koszt',
    description: 'Dostawca, limity i koszty asystenta oraz podsumowań AI w organizacji.',
    loadError: 'Nie udało się wczytać danych o zużyciu AI.',
    month: 'Miesiąc {month}',
    status: {
      provider: 'Dostawca',
      providerAnthropic: 'Anthropic Claude',
      providerMock: 'Tryb demonstracyjny',
      providerMockHint: 'Brak klucza API — odpowiedzi z szablonów na prawdziwych liczbach.',
      chatModel: 'Model czatu',
      summaryModel: 'Model podsumowań',
      limits: 'Limity',
      dailyPerUser: '{count} pytań / użytkownik / dzień',
      regenerations: '{count} odświeżeń podsumowań / użytkownik / dzień',
      budget: 'Budżet miesięczny: {amount}',
    },
    budget: {
      title: 'Koszt w tym miesiącu',
      of: 'z budżetu {amount}',
      used: 'Wykorzystano {pct}',
      projection: 'Prognoza na koniec miesiąca',
      projectionOk: 'Mieści się w budżecie',
      projectionOver: 'Przekroczy budżet o {amount}',
      day: 'Dzień {elapsed} z {total}',
    },
    kpi: {
      questions: 'Pytania do asystenta',
      summaries: 'Wygenerowane podsumowania',
      avgQuestion: 'Średni koszt pytania',
      avgSummary: 'Średni koszt podsumowania',
    },
    byDay: {
      title: 'Koszt dzienny',
      description: 'Wydatki na AI w bieżącym miesiącu (USD)',
      series: 'Koszt',
      questions: 'Pytania',
      summaries: 'Podsumowania',
      empty: 'Brak zużycia w tym miesiącu',
    },
    byUser: {
      title: 'Koszt wg użytkownika',
      description: 'Kto korzysta z asystenta najczęściej',
      user: 'Użytkownik',
      questions: 'Pytania',
      summaries: 'Podsumowania',
      cost: 'Koszt',
      share: 'Udział',
      empty: 'Brak zużycia',
      system: 'System (zadania automatyczne)',
    },
    byModel: {
      title: 'Koszt wg modelu',
      description: 'Wywołania i tokeny',
      model: 'Model',
      calls: 'Wywołania',
      input: 'Tokeny wejścia',
      output: 'Tokeny wyjścia',
      cache: 'Cache (odczyt)',
      cost: 'Koszt',
      empty: 'Brak wywołań',
    },
    mine: {
      title: 'Moje zużycie',
      today: 'Dziś: {used} z {limit} pytań',
      month: 'Ten miesiąc: {cost}',
    },
  },
};

export const aiEn: Shape<typeof aiPl> = {
  assistant: {
    title: 'Ask NEXOPS',
    subtitle: 'AI assistant grounded in your data',
    open: 'Open AI assistant',
    close: 'Close assistant',
    topbar: 'Ask NEXOPS',
    demoChip: 'Demo mode',
    demoChipHint: 'Answers are generated from templates on real figures from the system.',
    newChat: 'New conversation',
    welcomeTitle: 'How can I help?',
    welcomeBody: 'Ask about customers, sales, target attainment or alerts. I answer from the data within your access scope.',
    welcomeBodyLogistics: 'Ask about deliveries, routes, delays or failed deliveries. I answer from the logistics data.',
    welcomeBodyBoth: 'Ask about sales, customers, deliveries or routes. I answer from the data within your access scope.',
    suggestionsLabel: 'To get started',
    suggestionGroups: {
      sales: 'Sales',
      logistics: 'Logistics',
    },
    placeholder: 'Ask a question about your data…',
    send: 'Send',
    stop: 'Stop',
    copy: 'Copy answer',
    copied: 'Copied',
    stopped: 'Answer generation stopped.',
    truncated: 'The answer was shortened — ask for more details.',
    retry: 'Try again',
    thinking: 'Analysing data…',
    toolsUsed: 'Data checked: {count}',
    toolsRunning: 'Working…',
    you: 'You',
    assistantName: 'NEXOPS AI',
    footer: 'AI answers are based on NEXOPS data within your access scope.',
    remaining: 'Left today: {left} of {limit} questions',
    usageLink: 'Usage and cost',
    charsLeft: '{count} characters',
    shortcut: 'Ctrl J',
    scrollDown: 'Scroll to latest answer',
    errors: {
      dailyLimit: 'You have used the daily question limit ({limit}). It resets after midnight — until then the summaries on the home page are still available.',
      dailyLimitNoNumber: 'You have used the daily question limit. It resets after midnight.',
      monthlyBudget: 'The organisation\'s monthly AI budget has been used up. It renews at the start of next month — contact your administrator if it is urgent.',
      network: 'Could not reach the server. Check your connection and try again.',
      unavailable: 'The AI assistant is temporarily unavailable. Please try again shortly.',
      refused: 'The assistant cannot answer this question. Try rephrasing it.',
      badRequest: 'This message cannot be sent. Shorten the question and try again.',
      generic: 'Something went wrong while generating the answer.',
    },
    suggestions: {
      repCustomers: 'Which of my customers buy less than a month ago?',
      repToday: 'What should I do today?',
      repForecast: 'What does my forecast look like?',
      repAlerts: 'What are my most important alerts?',
      mgrWorstRep: 'Which salesperson has the weakest target attainment?',
      mgrWeek: 'What changed this week?',
      mgrDeclining: 'Which team customers buy less than a month ago?',
      mgrForecast: 'What is the end-of-month forecast?',
      boardTop: 'Which customers generate the most sales this month?',
      boardReceivables: 'What do overdue receivables look like?',
      logLate: 'How many deliveries are late today?',
      logRepeatFailed: 'Which customers have repeated failed deliveries?',
      logOverloaded: 'Which routes are overloaded tomorrow?',
      logWeek: 'How punctual were deliveries this week?',
    },
    tools: {
      search_customers: 'Looking up the customer…',
      customer_overview: 'Checking customer sales…',
      sales_summary: 'Calculating sales…',
      top_customers: 'Checking top customers…',
      declining_customers: 'Finding declining customers…',
      rep_performance: 'Comparing salespeople…',
      product_stats: 'Checking product sales…',
      list_alerts: 'Reviewing alerts…',
      receivables_summary: 'Checking receivables…',
      forecast: 'Calculating forecast and target attainment…',
      logistics_deliveries: 'Checking deliveries…',
      logistics_problems: 'Checking late and failed deliveries…',
      logistics_problem_customers: 'Finding customers with delivery problems…',
      logistics_route_load: 'Checking route load…',
    },
  },
  insight: {
    title: 'What changed and why',
    kind: {
      DAILY_REP: 'Today',
      WEEKLY_TEAM: 'Week · team',
      WEEKLY_BOARD: 'Week · board',
      LOGISTICS_DAILY: 'Logistics · today',
      LOGISTICS_WEEKLY: 'Logistics · week',
    },
    logisticsPeriod: 'Logistics summary period',
    logisticsDaily: 'Today',
    logisticsWeekly: 'Week',
    generatedAt: 'Generated {when}',
    basis: 'Period: {period}',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    actionsTitle: 'Recommended actions',
    actionsHint: 'Tick what is already done',
    markDone: 'Mark as done',
    markUndone: 'Mark as to do',
    askMore: 'Ask for details',
    emptyTitle: 'No summary yet',
    emptyBody: 'The summary for this period has not been prepared yet. Generate it now.',
    generate: 'Generate summary',
    loadError: 'Could not load the AI summary.',
    regenLimit: 'You have used the daily limit of summary refreshes. Try again tomorrow.',
    regenError: 'Could not refresh the summary.',
    regenerated: 'Summary refreshed',
    model: 'Model: {model}',
  },
  usage: {
    nav: 'AI — usage and cost',
    title: 'AI — usage and cost',
    description: 'Provider, limits and costs of the AI assistant and summaries across the organisation.',
    loadError: 'Could not load AI usage data.',
    month: 'Month {month}',
    status: {
      provider: 'Provider',
      providerAnthropic: 'Anthropic Claude',
      providerMock: 'Demo mode',
      providerMockHint: 'No API key — template answers on real figures.',
      chatModel: 'Chat model',
      summaryModel: 'Summary model',
      limits: 'Limits',
      dailyPerUser: '{count} questions / user / day',
      regenerations: '{count} summary refreshes / user / day',
      budget: 'Monthly budget: {amount}',
    },
    budget: {
      title: 'Cost this month',
      of: 'of {amount} budget',
      used: '{pct} used',
      projection: 'End-of-month projection',
      projectionOk: 'Within budget',
      projectionOver: 'Will exceed the budget by {amount}',
      day: 'Day {elapsed} of {total}',
    },
    kpi: {
      questions: 'Assistant questions',
      summaries: 'Generated summaries',
      avgQuestion: 'Average cost per question',
      avgSummary: 'Average cost per summary',
    },
    byDay: {
      title: 'Daily cost',
      description: 'AI spend in the current month (USD)',
      series: 'Cost',
      questions: 'Questions',
      summaries: 'Summaries',
      empty: 'No usage this month',
    },
    byUser: {
      title: 'Cost by user',
      description: 'Who uses the assistant most',
      user: 'User',
      questions: 'Questions',
      summaries: 'Summaries',
      cost: 'Cost',
      share: 'Share',
      empty: 'No usage',
      system: 'System (automatic jobs)',
    },
    byModel: {
      title: 'Cost by model',
      description: 'Calls and tokens',
      model: 'Model',
      calls: 'Calls',
      input: 'Input tokens',
      output: 'Output tokens',
      cache: 'Cache (read)',
      cost: 'Cost',
      empty: 'No calls',
    },
    mine: {
      title: 'My usage',
      today: 'Today: {used} of {limit} questions',
      month: 'This month: {cost}',
    },
  },
};
